import { WorkOrderData, ApprovalStatus, PendingWorkOrderData } from "../types";
import { mockPendingWorkOrdersData } from "./pendingWorkOrdersData";

// Enum for Change Request Status
export enum ChangeRequestStatus {
  ChangeRequested = "Changes Requested",
  UnderReview = "Under Review",
  AmendmentApproved = "Amendment Approved",
  AmendmentRejected = "Amendment Rejected",
  WorkOrderAmended = "Work Order Amended",
}

// Interface for Change Request Comments
export interface ChangeRequestComment {
  id: string;
  commentBy: string;
  commentRole: "Contractor" | "Buyer" | "System";
  commentDate: string;
  commentText: string;
  changeType: "Scope" | "Timeline" | "Cost" | "Technical" | "General";
  isInternal?: boolean;
}

// Interface for Change Request Files
export interface ChangeRequestFile {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedBy: string;
  uploadedDate: string;
  category: "Drawing" | "Specification" | "Photo" | "Document" | "Other";
  description?: string;
  url?: string;
}

// Interface for Work Order with Change Request data
export interface WorkOrderAmendmentData extends WorkOrderData {
  changeRequestStatus: ChangeRequestStatus;
  changeRequestDate: string;
  changeRequestedBy: string;
  changeRequestComments: ChangeRequestComment[];
  changeRequestFiles: ChangeRequestFile[];
  proposedChanges: {
    scopeChanges?: string;
    timelineChanges?: {
      newStartDate?: string;
      newEndDate?: string;
      reason?: string;
    };
    costChanges?: {
      additionalCost?: number;
      deductionCost?: number;
      netChange?: number;
      reason?: string;
    };
    technicalChanges?: string;
  };
  buyerReviewComments?: ChangeRequestComment[];
  amendmentHistory: {
    action: string;
    actionBy: string;
    actionDate: string;
    comments?: string;
  }[];
}

// Mock data for Work Orders with Change Requests
export const mockWorkOrderAmendmentData: WorkOrderAmendmentData[] = [
  {
    id: "wo-amend-001",
    workOrderNumber: "WO/2025-26/HYD/0042",
    linkedWorkRequest: "WR/2025-26/HYD/0015",
    linkedTender: "TN/2025-26/HYD/0008",
    contractDate: "2025-08-01",
    workOrderTitle: "Core & Shell - Tower B",
    workOrderDescription:
      "Complete structural work for Tower B including foundation and RCC work",
    siteId: "site-001",
    siteName: "HYD Site A",
    projectId: "proj-001",
    projectName: "Tower B",
    contractType: "Construction Work" as any,
    contractWith: "General Contractor" as any,
    contractorId: "cont-001",
    contractorName: "Galaxy Infra",
    workOrderOwner: "John Smith",
    plannedStartDate: "2025-08-10",
    plannedEndDate: "2026-02-15",
    approvalRequired: true,
    enableTax: true,
    taxApplicationLevel: "Line Level" as any,
    status: "Released" as any,
    workOrderLines: [
      {
        id: "wol-001",
        lineNumber: "001",
        workCategory: "Civil Work",
        subCategory: "Foundation",
        taskTitle: "Foundation Excavation",
        taskDescription: "Excavation for foundation up to required depth",
        quantity: 1200,
        uom: "Cum",
        weightagePercentage: 15,
        rate: 850,
        taxType: "GST",
        taxPercentage: 18,
        taxAmount: 183600,
        totalAmount: 1203600,
        source: "Project Management" as any,
        sourceId: "pm-task-001",
        sectionId: "sec-001",
      },
    ],
    generalConditions: [],
    paymentConditions: {
      mobilizationAdvance: {
        isPercentOfContract: true,
        percentageOfContract: 10,
        interestCharged: false,
        recoveryType: "Each RA Bill" as any,
      },
      retentionAmount: {
        isPercentOfRABill: true,
        percentageOfRABill: 5,
        releaseDate: "2026-08-15" as any,
      },
      onHoldAmount: {
        isPercentOfContract: false,
        releaseDate: "2026-02-15" as any,
      },
      payOnWorkCompletion: false,
    },
    totalValue: 1897280,
    taxAmount: 340000,
    grossValue: 2237280,
    createdAt: "2025-08-01",
    createdBy: "John Smith",

    // Change Request specific fields
    changeRequestStatus: ChangeRequestStatus.ChangeRequested,
    changeRequestDate: "2025-08-20",
    changeRequestedBy: "Galaxy Infra",
    changeRequestComments: [
      {
        id: "crc-001",
        commentBy: "Rajesh Kumar - Galaxy Infra",
        commentRole: "Contractor",
        commentDate: "2025-08-20",
        commentText:
          "We have encountered rock formation during excavation which was not anticipated in the original soil investigation report. This will require additional breaking work and specialized equipment, increasing both time and cost.",
        changeType: "Technical",
      },
      {
        id: "crc-002",
        commentBy: "Rajesh Kumar - Galaxy Infra",
        commentRole: "Contractor",
        commentDate: "2025-08-20",
        commentText:
          "Due to the rock breaking requirement, we need to extend the foundation work timeline by 15 days. Additional cost for rock breaking equipment and specialized labor will be ₹3,50,000.",
        changeType: "Cost",
      },
      {
        id: "crc-003",
        commentBy: "Rajesh Kumar - Galaxy Infra",
        commentRole: "Contractor",
        commentDate: "2025-08-21",
        commentText:
          "Please find attached the updated drawings showing the rock formation areas and proposed breaking methodology. Site photos also attached showing current excavation status.",
        changeType: "Technical",
      },
    ],
    changeRequestFiles: [
      {
        id: "crf-001",
        fileName: "Rock_Formation_Survey_Report.pdf",
        fileSize: 2560000,
        fileType: "application/pdf",
        uploadedBy: "Rajesh Kumar - Galaxy Infra",
        uploadedDate: "2025-08-20",
        category: "Document",
        description: "Detailed survey report showing rock formation areas",
      },
      {
        id: "crf-002",
        fileName: "Updated_Foundation_Drawing_Rev2.dwg",
        fileSize: 1024000,
        fileType: "application/dwg",
        uploadedBy: "Rajesh Kumar - Galaxy Infra",
        uploadedDate: "2025-08-20",
        category: "Drawing",
        description: "Revised foundation drawings with rock breaking details",
      },
      {
        id: "crf-003",
        fileName: "Site_Photos_Rock_Formation.zip",
        fileSize: 5120000,
        fileType: "application/zip",
        uploadedBy: "Rajesh Kumar - Galaxy Infra",
        uploadedDate: "2025-08-21",
        category: "Photo",
        description: "Current excavation photos showing rock formation",
      },
      {
        id: "crf-004",
        fileName: "Rock_Breaking_Equipment_Specs.pdf",
        fileSize: 890000,
        fileType: "application/pdf",
        uploadedBy: "Rajesh Kumar - Galaxy Infra",
        uploadedDate: "2025-08-21",
        category: "Specification",
        description:
          "Technical specifications of proposed rock breaking equipment",
      },
    ],
    proposedChanges: {
      scopeChanges:
        "Addition of rock breaking work in foundation excavation areas. Installation of specialized drainage system for rock formation areas.",
      timelineChanges: {
        newStartDate: "2025-08-10",
        newEndDate: "2026-03-02",
        reason:
          "Additional 15 days required for rock breaking work using specialized equipment",
      },
      costChanges: {
        additionalCost: 350000,
        deductionCost: 0,
        netChange: 350000,
        reason:
          "Rock breaking equipment rental, specialized labor, and additional drainage work",
      },
      technicalChanges:
        "Modified foundation design to accommodate rock formation. Added reinforcement for rock-foundation interface areas.",
    },
    amendmentHistory: [
      {
        action: "Change Request Initiated",
        actionBy: "Rajesh Kumar - Galaxy Infra",
        actionDate: "2025-08-20",
        comments: "Change request submitted due to unforeseen rock formation",
      },
      {
        action: "Documentation Updated",
        actionBy: "Rajesh Kumar - Galaxy Infra",
        actionDate: "2025-08-21",
        comments: "Added technical drawings and site photos",
      },
    ],
  },

  {
    id: "wo-amend-002",
    workOrderNumber: "WO/2025-26/BLR/0018",
    linkedWorkRequest: "WR/2025-26/BLR/0012",
    linkedTender: "TN/2025-26/BLR/0005",
    contractDate: "2025-07-15",
    workOrderTitle: "MEP Package",
    workOrderDescription: "Mechanical, Electrical & Plumbing work",
    siteId: "site-002",
    siteName: "BLR Site C",
    projectId: "proj-003",
    projectName: "Tower C",
    contractType: "Construction Work" as any,
    contractWith: "General Contractor" as any,
    contractorId: "cont-003",
    contractorName: "Zenith MEP",
    workOrderOwner: "Mike Johnson",
    plannedStartDate: "2025-09-01",
    plannedEndDate: "2026-05-31",
    approvalRequired: true,
    enableTax: true,
    taxApplicationLevel: "Work Order Level" as any,
    status: "Released" as any,
    workOrderLines: [],
    generalConditions: [],
    paymentConditions: {
      mobilizationAdvance: {
        isPercentOfContract: true,
        percentageOfContract: 15,
        interestCharged: true,
        interestRate: 12,
        recoveryType: "Final Bill" as any,
      },
      retentionAmount: {
        isPercentOfRABill: true,
        percentageOfRABill: 10,
        releaseDate: "2027-05-31" as any,
      },
      onHoldAmount: {
        isPercentOfContract: false,
        releaseDate: "2026-05-31" as any,
      },
      payOnWorkCompletion: true,
    },
    totalValue: 2923729,
    taxAmount: 526271,
    grossValue: 3450000,
    createdAt: "2025-07-15",
    createdBy: "Mike Johnson",

    changeRequestStatus: ChangeRequestStatus.ChangeRequested,
    changeRequestDate: "2025-08-22",
    changeRequestedBy: "Zenith MEP",
    changeRequestComments: [
      {
        id: "crc-004",
        commentBy: "Suresh Patel - Zenith MEP",
        commentRole: "Contractor",
        commentDate: "2025-08-22",
        commentText:
          "The client has requested upgrade of HVAC system from 3-star to 5-star energy rating as per new green building requirements. This requires higher capacity chillers and VRV systems.",
        changeType: "Scope",
      },
      {
        id: "crc-005",
        commentBy: "Suresh Patel - Zenith MEP",
        commentRole: "Contractor",
        commentDate: "2025-08-22",
        commentText:
          "Additional electrical load requirement due to upgraded HVAC will need transformer capacity increase from 1000 KVA to 1500 KVA. Main electrical panel also needs upgrading.",
        changeType: "Technical",
      },
      {
        id: "crc-006",
        commentBy: "Suresh Patel - Zenith MEP",
        commentRole: "Contractor",
        commentDate: "2025-08-23",
        commentText:
          "The scope change will add approximately ₹8,50,000 to the project cost and extend timeline by 3 weeks for procurement and installation of upgraded equipment.",
        changeType: "Cost",
      },
    ],
    changeRequestFiles: [
      {
        id: "crf-005",
        fileName: "5Star_HVAC_Technical_Specs.pdf",
        fileSize: 1800000,
        fileType: "application/pdf",
        uploadedBy: "Suresh Patel - Zenith MEP",
        uploadedDate: "2025-08-22",
        category: "Specification",
        description:
          "Technical specifications for 5-star energy rated HVAC system",
      },
      {
        id: "crf-006",
        fileName: "Load_Calculation_Updated.xlsx",
        fileSize: 450000,
        fileType: "application/xlsx",
        uploadedBy: "Suresh Patel - Zenith MEP",
        uploadedDate: "2025-08-22",
        category: "Document",
        description: "Updated electrical load calculations for upgraded HVAC",
      },
      {
        id: "crf-007",
        fileName: "Green_Building_Compliance_Certificate.pdf",
        fileSize: 720000,
        fileType: "application/pdf",
        uploadedBy: "Suresh Patel - Zenith MEP",
        uploadedDate: "2025-08-23",
        category: "Document",
        description: "Green building compliance requirements from client",
      },
    ],
    proposedChanges: {
      scopeChanges:
        "Upgrade HVAC system from 3-star to 5-star energy rating. Increase transformer capacity from 1000 KVA to 1500 KVA. Upgrade main electrical distribution panel.",
      timelineChanges: {
        newStartDate: "2025-09-01",
        newEndDate: "2026-06-21",
        reason:
          "3 weeks additional time required for upgraded equipment procurement and installation",
      },
      costChanges: {
        additionalCost: 850000,
        deductionCost: 0,
        netChange: 850000,
        reason:
          "Cost of upgraded HVAC equipment, higher capacity transformer, and enhanced electrical panels",
      },
      technicalChanges:
        "Enhanced HVAC system with VRV technology, upgraded electrical infrastructure to support increased load capacity.",
    },
    amendmentHistory: [
      {
        action: "Change Request Initiated",
        actionBy: "Suresh Patel - Zenith MEP",
        actionDate: "2025-08-22",
        comments:
          "Change request for HVAC system upgrade as per green building requirements",
      },
      {
        action: "Technical Documentation Submitted",
        actionBy: "Suresh Patel - Zenith MEP",
        actionDate: "2025-08-23",
        comments: "Submitted load calculations and compliance certificates",
      },
    ],
  },

  {
    id: "wo-amend-003",
    workOrderNumber: "WO/2025-26/CHN/0025",
    linkedWorkRequest: "WR/2025-26/CHN/0008",
    contractDate: "2025-08-10",
    workOrderTitle: "Foundation Work Package",
    workOrderDescription:
      "Complete foundation work including excavation and concrete pouring",
    siteId: "site-003",
    siteName: "Chennai Site D",
    projectId: "proj-004",
    projectName: "Residential Complex",
    contractType: "Construction Work" as any,
    contractWith: "General Contractor" as any,
    contractorId: "cont-004",
    contractorName: "L&T Civil Infra",
    workOrderOwner: "Sarah Wilson",
    plannedStartDate: "2025-08-20",
    plannedEndDate: "2025-11-30",
    approvalRequired: true,
    enableTax: true,
    status: "Released" as any,
    workOrderLines: [],
    generalConditions: [],
    paymentConditions: {
      mobilizationAdvance: {
        isPercentOfContract: true,
        percentageOfContract: 20,
        interestCharged: false,
        recoveryType: "Each RA Bill" as any,
      },
      retentionAmount: {
        isPercentOfRABill: true,
        percentageOfRABill: 5,
        releaseDate: "2026-11-30" as any,
      },
      onHoldAmount: {
        isPercentOfContract: false,
        releaseDate: "2025-11-30" as any,
      },
      payOnWorkCompletion: false,
    },
    totalValue: 1800000,
    taxAmount: 324000,
    grossValue: 2124000,
    createdAt: "2025-08-10",
    createdBy: "Sarah Wilson",

    changeRequestStatus: ChangeRequestStatus.UnderReview,
    changeRequestDate: "2025-08-23",
    changeRequestedBy: "L&T Civil Infra",
    changeRequestComments: [
      {
        id: "crc-007",
        commentBy: "Arun Krishnan - L&T Civil Infra",
        commentRole: "Contractor",
        commentDate: "2025-08-23",
        commentText:
          "Monsoon season has started earlier than expected with continuous heavy rainfall. Foundation work cannot proceed safely in current weather conditions as per safety protocols.",
        changeType: "Timeline",
      },
      {
        id: "crc-008",
        commentBy: "Arun Krishnan - L&T Civil Infra",
        commentRole: "Contractor",
        commentDate: "2025-08-23",
        commentText:
          "We need to postpone foundation work start date by 2 weeks until weather conditions improve. No additional cost impact, only timeline adjustment required.",
        changeType: "Timeline",
      },
    ],
    changeRequestFiles: [
      {
        id: "crf-008",
        fileName: "Weather_Forecast_Report_Aug2025.pdf",
        fileSize: 1200000,
        fileType: "application/pdf",
        uploadedBy: "Arun Krishnan - L&T Civil Infra",
        uploadedDate: "2025-08-23",
        category: "Document",
        description: "IMD weather forecast showing extended monsoon period",
      },
      {
        id: "crf-009",
        fileName: "Safety_Protocol_Monsoon_Guidelines.pdf",
        fileSize: 890000,
        fileType: "application/pdf",
        uploadedBy: "Arun Krishnan - L&T Civil Infra",
        uploadedDate: "2025-08-23",
        category: "Document",
        description:
          "Company safety guidelines for monsoon season construction",
      },
    ],
    proposedChanges: {
      scopeChanges:
        "No scope changes required. Work to proceed as planned once weather conditions improve.",
      timelineChanges: {
        newStartDate: "2025-09-05",
        newEndDate: "2025-12-15",
        reason: "Delay due to extended monsoon season and safety protocols",
      },
      costChanges: {
        additionalCost: 0,
        deductionCost: 0,
        netChange: 0,
        reason:
          "No cost impact, only timeline adjustment due to weather conditions",
      },
    },
    buyerReviewComments: [
      {
        id: "brc-001",
        commentBy: "Sarah Wilson - Site Engineer",
        commentRole: "Buyer",
        commentDate: "2025-08-24",
        commentText:
          "Weather delay is understandable and justified. However, we need confirmation on resource availability once work resumes to avoid further delays.",
        changeType: "General",
        isInternal: true,
      },
    ],
    amendmentHistory: [
      {
        action: "Change Request Initiated",
        actionBy: "Arun Krishnan - L&T Civil Infra",
        actionDate: "2025-08-23",
        comments:
          "Timeline adjustment requested due to monsoon weather conditions",
      },
      {
        action: "Under Review",
        actionBy: "Sarah Wilson - Site Engineer",
        actionDate: "2025-08-24",
        comments: "Change request under review by engineering team",
      },
    ],
  },
];

// Helper functions
export const getWorkOrdersInChangeRequest = (): WorkOrderAmendmentData[] => {
  return mockWorkOrderAmendmentData.filter(
    (wo) => wo.changeRequestStatus === ChangeRequestStatus.ChangeRequested
  );
};

export const getWorkOrderAmendmentById = (
  id: string
): WorkOrderAmendmentData | undefined => {
  return mockWorkOrderAmendmentData.find((wo) => wo.id === id);
};

export const getPendingWorkOrderById = (
  id: string
): PendingWorkOrderData | undefined => {
  return mockPendingWorkOrdersData.find((wo) => wo.id === id);
};

export const getChangeRequestStatusColor = (
  status: ChangeRequestStatus
): string => {
  switch (status) {
    case ChangeRequestStatus.ChangeRequested:
      return "orange";
    case ChangeRequestStatus.UnderReview:
      return "blue";
    case ChangeRequestStatus.AmendmentApproved:
      return "green";
    case ChangeRequestStatus.AmendmentRejected:
      return "red";
    case ChangeRequestStatus.WorkOrderAmended:
      return "cyan";
    default:
      return "default";
  }
};

export const getFileIcon = (fileType: string): string => {
  if (fileType.includes("pdf")) return "📄";
  if (fileType.includes("dwg") || fileType.includes("autocad")) return "📐";
  if (
    fileType.includes("image") ||
    fileType.includes("jpg") ||
    fileType.includes("png")
  )
    return "🖼️";
  if (fileType.includes("zip") || fileType.includes("rar")) return "📦";
  if (fileType.includes("excel") || fileType.includes("xlsx")) return "📊";
  if (fileType.includes("word") || fileType.includes("docx")) return "📝";
  return "📎";
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
